/**
 * ConsoleLayout — authenticated shell: dark sidebar (permanent on md+, drawer
 * on mobile) + sticky topbar + scrollable <Outlet/>. Mirrors the prototype's
 * web-native layout (assets/css/app.css .sidebar / .topbar).
 */
import React, { useState } from 'react';
import {
  Box, Typography, Drawer, IconButton, Avatar, Menu, MenuItem, ListItemIcon, Divider, Tooltip,
} from '@mui/material';
import { MenuRounded, LogoutRounded, PersonRounded } from '@mui/icons-material';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { colors, brand } from '../theme/theme';
import { NAV_ITEMS, SECTIONS, pageMetaFor } from './navConfig';
import { getDisplayName, getEmail, getRoleName, clearSession, initials } from '../auth/session';

const SIDEBAR_W = 252;
const TOPBAR_H = 64;

const ConsoleLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const meta = pageMetaFor(location.pathname);
  const displayName = getDisplayName();
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => { clearSession(); navigate('/login', { replace: true }); };

  const sidebarInner = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, pb: 2.25, color: '#fff' }}>
        <Box component="img" src="/logo192.png" alt="BaseQueue" sx={{ width: 36, height: 36, borderRadius: '10px', objectFit: 'cover' }} />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>BaseQueue</Typography>
          <Typography sx={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Admin Console</Typography>
        </Box>
      </Box>

      <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, overflowY: 'auto' }}>
        {SECTIONS.map((section) => (
          <React.Fragment key={section}>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748B', px: 1.5, pt: 1.75, pb: 0.75, fontWeight: 700 }}>
              {section}
            </Typography>
            {NAV_ITEMS.filter((it) => it.section === section).map((it) => {
              const active = isActive(it.path);
              return (
                <Box
                  key={it.path}
                  component={Link}
                  to={it.path}
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: '11px',
                    color: active ? '#fff' : brand.sidebarFg, bgcolor: active ? brand.sidebarActive : 'transparent',
                    fontWeight: 500, fontSize: 14, textDecoration: 'none', transition: 'background .12s, color .12s',
                    '&:hover': { bgcolor: active ? brand.sidebarActive : 'rgba(255,255,255,.05)', color: '#fff' },
                    '& svg': { fontSize: 20 },
                  }}
                >
                  {it.icon}
                  <span>{it.label}</span>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      <Box sx={{ borderTop: '1px solid rgba(255,255,255,.08)', pt: 1.75, display: 'flex', alignItems: 'center', gap: 1.25, px: 0.5 }}>
        <Avatar sx={{ width: 34, height: 34, fontSize: 13, background: brand.gradBrand }}>{initials(displayName)}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{displayName}</Typography>
          <Typography noWrap sx={{ color: '#64748B', fontSize: 12 }}>{getEmail()}</Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: colors.backgroundDefault }}>
      {/* Permanent sidebar (md+) */}
      <Box component="aside" sx={{ width: SIDEBAR_W, flexShrink: 0, bgcolor: brand.sidebarBg, display: { xs: 'none', md: 'block' }, position: 'sticky', top: 0, height: '100vh' }}>
        {sidebarInner}
      </Box>

      {/* Mobile drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} sx={{ display: { md: 'none' } }}
        PaperProps={{ sx: { width: SIDEBAR_W, bgcolor: brand.sidebarBg, color: brand.sidebarFg } }}>
        {sidebarInner}
      </Drawer>

      {/* Main column */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box component="header" sx={{ height: TOPBAR_H, bgcolor: colors.backgroundPaper, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 3.5 }, position: 'sticky', top: 0, zIndex: 20 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ display: { md: 'none' } }} aria-label="Open navigation"><MenuRounded /></IconButton>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{meta.title}</Typography>
              {meta.crumbs && <Typography sx={{ fontSize: 12.5, color: colors.textTertiary }}>{meta.crumbs}</Typography>}
            </Box>
          </Box>

          <Tooltip title={displayName}>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.25 }} aria-label="Account menu">
              <Avatar sx={{ width: 38, height: 38, fontSize: 14, background: brand.gradBrand }}>{initials(displayName)}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{displayName}</Typography>
              <Typography variant="caption" color="text.secondary">{getRoleName()}</Typography>
            </Box>
            <Divider />
            <MenuItem disabled>
              <ListItemIcon><PersonRounded fontSize="small" /></ListItemIcon>{getEmail()}
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: colors.error }}>
              <ListItemIcon><LogoutRounded fontSize="small" sx={{ color: colors.error }} /></ListItemIcon>Logout
            </MenuItem>
          </Menu>
        </Box>

        <Box component="main" sx={{ p: { xs: 2, sm: 3.5 }, maxWidth: 1320, width: '100%', mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default ConsoleLayout;
